import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { appendAIPaneFromCurrentUrl } from '../../utils/aiPaneUrl';
import MergeProgressNotification from '../FileMergeModal/MergeProgressNotification';
import {
  assemblyDocumentId,
  firstSerializerError,
} from '../FileMergeModal/FileMergeModalContainerUtils';
import {
  createAssemblyJob,
  pollAssemblyJob,
} from '../../service/assemblyService';
import { getDocumentDetail } from '../../service/matter';
import {
  fetchMatterDetail,
  fetchMatterDetailList,
  postHighlightedMatter,
} from '../../slice/MatterDetailsSlice';
import { getNameWithoutExtension } from '../TabView/TabViewUtils';

/**
 * Shared PDF tooling for the matter documents table:
 *
 *   exportAsPdf(record)     one-click Office → PDF next to the original,
 *                           via a single-item server-side assembly job
 *   openPageBuilder(records) navigate to the page-level PDF builder,
 *                           seeded with the given records
 *   notification            floating progress element (render it once)
 *
 * Matter/folder identity mirrors FileMergeModalContainer exactly: the
 * tree's id is the Salesforce text id the assembly API resolves, and the
 * folder being browsed (websocket.currentFolderId, F-prefixed) is both
 * the output destination and the list-refresh scope.
 */
const usePdfTools = ({ navigate, matterUrlId, listContext = null }) => {
  const dispatch = useDispatch();
  const { matterDetails, matterDetailsTree, docListingPage } = useSelector(
    (state) => state.matterDetails
  );
  const currentFolderId = useSelector(
    (state) => state.websocket?.currentFolderId
  );

  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState(null);
  const [exportedDoc, setExportedDoc] = useState(null);

  const currentMatterId =
    matterDetailsTree?.id || matterDetails?.id || matterDetails?.sf_id;

  const scopedFolder = () =>
    currentFolderId && currentFolderId !== 'root'
      ? String(currentFolderId).startsWith('F')
        ? String(currentFolderId)
        : `F${currentFolderId}`
      : undefined;

  const exportAsPdf = async (record) => {
    if (isExporting) return;
    const documentId = assemblyDocumentId(record);
    if (!documentId) {
      setError('This document could not be identified for export.');
      return;
    }
    if (!currentMatterId) {
      setError('Matter ID is required');
      return;
    }
    const folder = scopedFolder();
    const outputName = `${getNameWithoutExtension(record?.name || 'Document')}.pdf`;

    setIsExporting(true);
    setIsComplete(false);
    setError(null);
    setExportedDoc(null);
    setProgress(5);
    try {
      const { job_id: jobId } = await createAssemblyJob({
        matter: currentMatterId,
        outputName,
        folder,
        items: [{ document_id: documentId }],
      });
      let simulated = 15;
      const job = await pollAssemblyJob(jobId, {
        onTick: (tick) => {
          if (tick.status === 'queued') setProgress(15);
          else if (tick.status === 'running') {
            simulated = Math.min(simulated + 7, 90);
            setProgress(simulated);
          }
        },
      });
      if (job.status === 'failed') {
        throw new Error(job.error_detail || 'Failed to export the PDF');
      }
      const detail = await getDocumentDetail(job.output_document_id);
      const document = detail?.data;

      // Same dual refresh as merge: sidebar tree + the open list view.
      dispatch(fetchMatterDetail(currentMatterId));
      dispatch(
        fetchMatterDetailList({
          id: currentMatterId,
          page: listContext?.page || docListingPage || 1,
          sort_direction: listContext?.sort_direction,
          sort_by: listContext?.sort_by,
          ...(folder ? { folder } : {}),
        })
      );

      if (!document?.relative_path) {
        throw new Error(
          'The PDF was created but could not be loaded — refresh the matter to find it.'
        );
      }
      setExportedDoc(document);
      setProgress(100);
      setIsComplete(true);
      setIsExporting(false);
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        firstSerializerError(err?.response?.data) ||
        err?.message ||
        'Failed to export the PDF';
      setError(message);
      setIsExporting(false);
      setProgress(0);
    }
  };

  const openPageBuilder = (records) => {
    const seedRecords = (Array.isArray(records) ? records : [records]).filter(
      Boolean
    );
    navigate(`/matter/${matterUrlId}/pdf-builder`, {
      state: { seedRecords, folder: scopedFolder() || null },
    });
  };

  const handleOpenExported = () => {
    if (!exportedDoc?.relative_path) return;
    const userId = Number(localStorage.getItem('user_id'));
    dispatch(
      postHighlightedMatter({
        matterId: exportedDoc.matter,
        docId: exportedDoc.unique_id?.substring(1),
        docType: 'document',
        tabAction: {
          usr_id: userId,
          document_id: String(exportedDoc.unique_id?.substring(1)),
          matter_id: String(exportedDoc.matter),
          action: 'open',
        },
      })
    ).then(() => {
      navigate(
        appendAIPaneFromCurrentUrl(`/matter${exportedDoc.relative_path}`)
      );
    });
  };

  const closeNotification = () => {
    setIsExporting(false);
    setIsComplete(false);
    setError(null);
    setProgress(0);
  };

  const notification =
    isExporting || isComplete || error ? (
      <MergeProgressNotification
        progress={progress}
        isComplete={isComplete}
        mergedFileUrl={exportedDoc?.relative_path || null}
        error={error}
        onClose={closeNotification}
        onOpenFile={handleOpenExported}
        inProgressLabel="Exporting as PDF."
        successLabel="Exported as PDF"
      />
    ) : null;

  return { exportAsPdf, openPageBuilder, notification, isExporting };
};

export default usePdfTools;
